import React, { useState, useEffect } from 'react';
import { products } from '../data/products';
import ProductCard from './ProductCard';
import SkeletonLoader from './SkeletonLoader';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiWifiOff } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const DashboardGrid = ({ onNotify }) => {
  const [filter, setFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const categories = ['ALL', 'POWER', 'CABLE', 'ADAPTER'];

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, [filter, search]);

  const filteredProducts = products.filter(p => {
    const matchesFilter = filter === 'ALL' || p.category.toUpperCase() === filter;
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) || 
                          p.subtitle.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <section className="py-12 md:py-20 px-4 max-w-7xl mx-auto" id="modules">
      <div className="flex flex-col gap-8 mb-12 border-b border-graphite-700 pb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div>
            <h2 className="fids-text text-3xl md:text-5xl font-black italic uppercase tracking-tighter text-white">
              Inventory <span className="text-electric">Scan</span>
            </h2>
            <p className="fids-text text-[10px] text-gray-500 mt-2 uppercase tracking-widest">
              Status: {loading ? 'SCANNING_IN_PROGRESS...' : 'DATABASE_ONLINE // SYNCED'}
            </p>
          </div>
          
          <div className="w-full md:w-auto flex flex-col sm:flex-row gap-4">
            <div className="relative group flex-grow">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-electric transition-colors" />
              <input 
                type="text"
                placeholder="SEARCH_MODULE..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full md:w-64 bg-graphite-900 border border-graphite-700 p-2 pl-10 fids-text text-xs focus:border-electric outline-none transition-all placeholder:opacity-30"
                aria-label="Search modules"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 scrollbar-hide">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-2 fids-text text-[10px] font-bold border whitespace-nowrap transition-all focus:ring-2 focus:ring-electric/50 outline-none ${
                    filter === cat 
                    ? 'bg-electric text-graphite-900 border-electric' 
                    : 'border-graphite-700 text-gray-500 hover:border-gray-500 hover:text-white'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-[400px] relative">
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div 
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {[1, 2, 3, 4, 5, 6].map(i => <SkeletonLoader key={i} />)}
            </motion.div>
          ) : filteredProducts.length > 0 ? (
            <motion.div 
              key="grid"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  onAdd={() => onNotify(`${product.name.toUpperCase()} // MODULE_DEPLOYED`, 'success')}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div 
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-20 text-center"
            >
              <div className="p-6 bg-graphite-800 rounded-sm border border-graphite-700 mb-6">
                <SafeIcon icon={FiWifiOff} className="text-5xl text-gray-600 mb-4 mx-auto" />
                <h3 className="fids-text text-xl font-bold text-white mb-2 uppercase">Zero_Results_Found</h3>
                <p className="fids-text text-xs text-gray-500 max-w-xs">No modules detected matching your current query parameters. Reset filters to continue.</p>
              </div>
              <button 
                onClick={() => { setFilter('ALL'); setSearch(''); }}
                className="fids-text text-[10px] text-electric hover:underline uppercase tracking-widest"
              >
                {'>'} RESET_DATABASE_SEARCH
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default DashboardGrid;